<template>
    <Card :header="$t('filters')">

        <form @keyup.enter="parentComponent.filterData()">

            <b-form-group :label="$t('id')" label-cols-sm="3">
                <b-form-input v-model="parentComponent.filters.id" :disabled="loading" />
            </b-form-group>

            <b-form-group :label="$t('offerId')" label-cols-sm="3">
                <b-form-input v-model="parentComponent.filters.offerId" :disabled="loading" />
            </b-form-group>

            <b-form-group :label="$t('ean')" label-cols-sm="3">
                <b-form-input v-model="parentComponent.filters.ean" :disabled="loading" />
            </b-form-group>

            <b-form-group :label="$t('name')" label-cols-sm="3">
                <b-form-input v-model="parentComponent.filters.name" :disabled="loading" />
            </b-form-group>

            <b-form-group :label="$t('info')" label-cols-sm="3">
                <b-form-input v-model="parentComponent.filters.info" :disabled="loading" />
            </b-form-group>

            <b-form-group :label="$t('feedId')" label-cols-sm="3">
                <b-form-input v-model="parentComponent.filters.feed" :disabled="loading" />
            </b-form-group>

            <b-form-group :label="$t('campaignId')" label-cols-sm="3">
                <b-form-input v-model="parentComponent.filters.campaignId" :disabled="loading" />
            </b-form-group>

            <b-form-group :label="$t('productId')" label-cols-sm="3">
                <b-form-input v-model="parentComponent.filters.productId" :disabled="loading" />
            </b-form-group>

            <b-form-group :label="$t('risk')" label-cols-sm="3">
                <div class="d-flex gap-3">
                    <b-input-group :prepend="$t('from')">
                        <b-form-input
                            v-model.number="parentComponent.filters.riskScoreFrom"
                            v-decimal="{
                                min: riskMinVal,
                                max: parentComponent.filters.riskScoreTo ?? riskMaxVal,
                                precision: 2,
                                step: 0.01,
                            }"
                            :disabled="loading"
                        />
                        <b-input-group-append>
                            <span class="input-group-text">
                                <ButtonIcon
                                    v-b-tooltip.hover.noninteractive
                                    :title="$t('clear')"
                                    icon="x"
                                    :icon-size="14"
                                    size="sm"
                                    variant="link"
                                    class="p-0 rounded-max text-secondary line-height-1"
                                    pill
                                    :disabled="loading"
                                    @click="parentComponent.filters.riskScoreFrom = null"
                                />
                            </span>
                        </b-input-group-append>
                    </b-input-group>
                    <b-input-group :prepend="$t('to')">
                        <b-form-input
                            v-model.number="parentComponent.filters.riskScoreTo"
                            v-decimal="{
                                min: parentComponent.filters.riskScoreFrom ?? riskMinVal,
                                max: riskMaxVal,
                                precision: 2,
                                step: 0.01,
                            }"
                            :disabled="loading"
                        />
                        <b-input-group-append>
                            <span class="input-group-text">
                                <ButtonIcon
                                    v-b-tooltip.hover.noninteractive
                                    :title="$t('clear')"
                                    icon="x"
                                    :icon-size="14"
                                    size="sm"
                                    variant="link"
                                    class="p-0 rounded-max text-secondary line-height-1"
                                    pill
                                    :disabled="loading"
                                    @click="parentComponent.filters.riskScoreTo = null"
                                />
                            </span>
                        </b-input-group-append>
                    </b-input-group>
                </div>
            </b-form-group>

            <b-form-group :label="`${$t('offerRiskScores')} - ${$t('riskInfoSimilarity')}`" label-cols-sm="3">
                <div class="d-flex gap-3">
                    <b-input-group :prepend="$t('from')">
                        <b-form-input
                            v-model.number="parentComponent.filters.offerRiskScoresFrom.info_similarity"
                            v-decimal="{
                                min: riskMinVal,
                                max: parentComponent.filters.offerRiskScoresTo.info_similarity ?? riskMaxVal,
                                precision: 2,
                                step: 0.01,
                            }"
                            :disabled="loading"
                        />
                        <b-input-group-append>
                            <span class="input-group-text">
                                <ButtonIcon
                                    v-b-tooltip.hover.noninteractive
                                    :title="$t('clear')"
                                    icon="x"
                                    :icon-size="14"
                                    size="sm"
                                    variant="link"
                                    class="p-0 rounded-max text-secondary line-height-1"
                                    pill
                                    :disabled="loading"
                                    @click="parentComponent.filters.offerRiskScoresFrom.info_similarity = null"
                                />
                            </span>
                        </b-input-group-append>
                    </b-input-group>
                    <b-input-group :prepend="$t('to')">
                        <b-form-input
                            v-model.number="parentComponent.filters.offerRiskScoresTo.info_similarity"
                            v-decimal="{
                                min: parentComponent.filters.offerRiskScoresFrom.info_similarity ?? riskMinVal,
                                max: riskMaxVal,
                                precision: 2,
                                step: 0.01,
                            }"
                            :disabled="loading"
                        />
                        <b-input-group-append>
                            <span class="input-group-text">
                                <ButtonIcon
                                    v-b-tooltip.hover.noninteractive
                                    :title="$t('clear')"
                                    icon="x"
                                    :icon-size="14"
                                    size="sm"
                                    variant="link"
                                    class="p-0 rounded-max text-secondary line-height-1"
                                    pill
                                    :disabled="loading"
                                    @click="parentComponent.filters.offerRiskScoresTo.info_similarity = null"
                                />
                            </span>
                        </b-input-group-append>
                    </b-input-group>
                </div>
            </b-form-group>

            <b-form-group :label="`${$t('offerRiskScores')} - ${$t('riskNameSimilarity')}`" label-cols-sm="3">
                <div class="d-flex gap-3">
                    <b-input-group :prepend="$t('from')">
                        <b-form-input
                            v-model.number="parentComponent.filters.offerRiskScoresFrom.name_similarity"
                            v-decimal="{
                                min: riskMinVal,
                                max: parentComponent.filters.offerRiskScoresTo.name_similarity ?? riskMaxVal,
                                precision: 2,
                                step: 0.01,
                            }"
                            :disabled="loading"
                        />
                        <b-input-group-append>
                            <span class="input-group-text">
                                <ButtonIcon
                                    v-b-tooltip.hover.noninteractive
                                    :title="$t('clear')"
                                    icon="x"
                                    :icon-size="14"
                                    size="sm"
                                    variant="link"
                                    class="p-0 rounded-max text-secondary line-height-1"
                                    pill
                                    :disabled="loading"
                                    @click="parentComponent.filters.offerRiskScoresFrom.name_similarity = null"
                                />
                            </span>
                        </b-input-group-append>
                    </b-input-group>
                    <b-input-group :prepend="$t('to')">
                        <b-form-input
                            v-model.number="parentComponent.filters.offerRiskScoresTo.name_similarity"
                            v-decimal="{
                                min: parentComponent.filters.offerRiskScoresFrom.name_similarity ?? riskMinVal,
                                max: riskMaxVal,
                                precision: 2,
                                step: 0.01,
                            }"
                            :disabled="loading"
                        />
                        <b-input-group-append>
                            <span class="input-group-text">
                                <ButtonIcon
                                    v-b-tooltip.hover.noninteractive
                                    :title="$t('clear')"
                                    icon="x"
                                    :icon-size="14"
                                    size="sm"
                                    variant="link"
                                    class="p-0 rounded-max text-secondary line-height-1"
                                    pill
                                    :disabled="loading"
                                    @click="parentComponent.filters.offerRiskScoresTo.name_similarity = null"
                                />
                            </span>
                        </b-input-group-append>
                    </b-input-group>
                </div>
            </b-form-group>

            <b-form-group :label="`${$t('offerRiskScores')} - ${$t('riskOfferQuantity')}`" label-cols-sm="3">
                <div class="d-flex gap-3">
                    <b-input-group :prepend="$t('from')">
                        <b-form-input
                            v-model.number="parentComponent.filters.offerRiskScoresFrom.offer_quantity"
                            v-decimal="{
                                min: riskMinVal,
                                max: parentComponent.filters.offerRiskScoresTo.offer_quantity ?? riskMaxVal,
                                precision: 2,
                                step: 0.01,
                            }"
                            :disabled="loading"
                        />
                        <b-input-group-append>
                            <span class="input-group-text">
                                <ButtonIcon
                                    v-b-tooltip.hover.noninteractive
                                    :title="$t('clear')"
                                    icon="x"
                                    :icon-size="14"
                                    size="sm"
                                    variant="link"
                                    class="p-0 rounded-max text-secondary line-height-1"
                                    pill
                                    :disabled="loading"
                                    @click="parentComponent.filters.offerRiskScoresFrom.offer_quantity = null"
                                />
                            </span>
                        </b-input-group-append>
                    </b-input-group>
                    <b-input-group :prepend="$t('to')">
                        <b-form-input
                            v-model.number="parentComponent.filters.offerRiskScoresTo.offer_quantity"
                            v-decimal="{
                                min: parentComponent.filters.offerRiskScoresFrom.offer_quantity ?? riskMinVal,
                                max: riskMaxVal,
                                precision: 2,
                                step: 0.01,
                            }"
                            :disabled="loading"
                        />
                        <b-input-group-append>
                            <span class="input-group-text">
                                <ButtonIcon
                                    v-b-tooltip.hover.noninteractive
                                    :title="$t('clear')"
                                    icon="x"
                                    :icon-size="14"
                                    size="sm"
                                    variant="link"
                                    class="p-0 rounded-max text-secondary line-height-1"
                                    pill
                                    :disabled="loading"
                                    @click="parentComponent.filters.offerRiskScoresTo.offer_quantity = null"
                                />
                            </span>
                        </b-input-group-append>
                    </b-input-group>
                </div>
            </b-form-group>

            <b-form-group :label="`${$t('productRiskScores')} - ${$t('riskExistingVariant')}`" label-cols-sm="3">
                <div class="d-flex gap-3">
                    <b-input-group :prepend="$t('from')">
                        <b-form-input
                            v-model.number="parentComponent.filters.productRiskScoresFrom.existing_variant"
                            v-decimal="{
                                min: riskMinVal,
                                max: parentComponent.filters.productRiskScoresTo.existing_variant ?? riskMaxVal,
                                precision: 2,
                                step: 0.01,
                            }"
                            :disabled="loading"
                        />
                        <b-input-group-append>
                            <span class="input-group-text">
                                <ButtonIcon
                                    v-b-tooltip.hover.noninteractive
                                    :title="$t('clear')"
                                    icon="x"
                                    :icon-size="14"
                                    size="sm"
                                    variant="link"
                                    class="p-0 rounded-max text-secondary line-height-1"
                                    pill
                                    :disabled="loading"
                                    @click="parentComponent.filters.productRiskScoresFrom.existing_variant = null"
                                />
                            </span>
                        </b-input-group-append>
                    </b-input-group>
                    <b-input-group :prepend="$t('to')">
                        <b-form-input
                            v-model.number="parentComponent.filters.productRiskScoresTo.existing_variant"
                            v-decimal="{
                                min: parentComponent.filters.productRiskScoresFrom.existing_variant ?? riskMinVal,
                                max: riskMaxVal,
                                precision: 2,
                                step: 0.01,
                            }"
                            :disabled="loading"
                        />
                        <b-input-group-append>
                            <span class="input-group-text">
                                <ButtonIcon
                                    v-b-tooltip.hover.noninteractive
                                    :title="$t('clear')"
                                    icon="x"
                                    :icon-size="14"
                                    size="sm"
                                    variant="link"
                                    class="p-0 rounded-max text-secondary line-height-1"
                                    pill
                                    :disabled="loading"
                                    @click="parentComponent.filters.productRiskScoresTo.existing_variant = null"
                                />
                            </span>
                        </b-input-group-append>
                    </b-input-group>
                </div>
            </b-form-group>

            <b-form-group :label="`${$t('productRiskScores')} - ${$t('riskInfoSimilarity')}`" label-cols-sm="3">
                <div class="d-flex gap-3">
                    <b-input-group :prepend="$t('from')">
                        <b-form-input
                            v-model.number="parentComponent.filters.productRiskScoresFrom.info_similarity"
                            v-decimal="{
                                min: riskMinVal,
                                max: parentComponent.filters.productRiskScoresTo.info_similarity ?? riskMaxVal,
                                precision: 2,
                                step: 0.01,
                            }"
                            :disabled="loading"
                        />
                        <b-input-group-append>
                            <span class="input-group-text">
                                <ButtonIcon
                                    v-b-tooltip.hover.noninteractive
                                    :title="$t('clear')"
                                    icon="x"
                                    :icon-size="14"
                                    size="sm"
                                    variant="link"
                                    class="p-0 rounded-max text-secondary line-height-1"
                                    pill
                                    :disabled="loading"
                                    @click="parentComponent.filters.productRiskScoresFrom.info_similarity = null"
                                />
                            </span>
                        </b-input-group-append>
                    </b-input-group>
                    <b-input-group :prepend="$t('to')">
                        <b-form-input
                            v-model.number="parentComponent.filters.productRiskScoresTo.info_similarity"
                            v-decimal="{
                                min: parentComponent.filters.productRiskScoresFrom.info_similarity ?? riskMinVal,
                                max: riskMaxVal,
                                precision: 2,
                                step: 0.01,
                            }"
                            :disabled="loading"
                        />
                        <b-input-group-append>
                            <span class="input-group-text">
                                <ButtonIcon
                                    v-b-tooltip.hover.noninteractive
                                    :title="$t('clear')"
                                    icon="x"
                                    :icon-size="14"
                                    size="sm"
                                    variant="link"
                                    class="p-0 rounded-max text-secondary line-height-1"
                                    pill
                                    :disabled="loading"
                                    @click="parentComponent.filters.productRiskScoresTo.info_similarity = null"
                                />
                            </span>
                        </b-input-group-append>
                    </b-input-group>
                </div>
            </b-form-group>

            <b-form-group :label="`${$t('productRiskScores')} - ${$t('riskNameSimilarity')}`" label-cols-sm="3">
                <div class="d-flex gap-3">
                    <b-input-group :prepend="$t('from')">
                        <b-form-input
                            v-model.number="parentComponent.filters.productRiskScoresFrom.name_similarity"
                            v-decimal="{
                                min: riskMinVal,
                                max: parentComponent.filters.productRiskScoresTo.name_similarity ?? riskMaxVal,
                                precision: 2,
                                step: 0.01,
                            }"
                            :disabled="loading"
                        />
                        <b-input-group-append>
                            <span class="input-group-text">
                                <ButtonIcon
                                    v-b-tooltip.hover.noninteractive
                                    :title="$t('clear')"
                                    icon="x"
                                    :icon-size="14"
                                    size="sm"
                                    variant="link"
                                    class="p-0 rounded-max text-secondary line-height-1"
                                    pill
                                    :disabled="loading"
                                    @click="parentComponent.filters.productRiskScoresFrom.name_similarity = null"
                                />
                            </span>
                        </b-input-group-append>
                    </b-input-group>
                    <b-input-group :prepend="$t('to')">
                        <b-form-input
                            v-model.number="parentComponent.filters.productRiskScoresTo.name_similarity"
                            v-decimal="{
                                min: parentComponent.filters.productRiskScoresFrom.name_similarity ?? riskMinVal,
                                max: riskMaxVal,
                                precision: 2,
                                step: 0.01,
                            }"
                            :disabled="loading"
                        />
                        <b-input-group-append>
                            <span class="input-group-text">
                                <ButtonIcon
                                    v-b-tooltip.hover.noninteractive
                                    :title="$t('clear')"
                                    icon="x"
                                    :icon-size="14"
                                    size="sm"
                                    variant="link"
                                    class="p-0 rounded-max text-secondary line-height-1"
                                    pill
                                    :disabled="loading"
                                    @click="parentComponent.filters.productRiskScoresTo.name_similarity = null"
                                />
                            </span>
                        </b-input-group-append>
                    </b-input-group>
                </div>
            </b-form-group>

            <div class="d-flex gap-3">
                <ButtonIcon :disabled="loading" @click="parentComponent.filterData()">
                    {{ $t('filter') }}
                </ButtonIcon>
                <ButtonIcon variant="light" :disabled="loading" @click="parentComponent.resetData()">
                    {{ $t('reset') }}
                </ButtonIcon>
            </div>

        </form>
    </Card>
</template>



<script>
    export default {
        name: 'Filters',
        inject: {
            parentComponent: {
                default: null,
            },
        },
        data() {
            return {
                riskMinVal: 0,
                riskMaxVal: 10,
            };
        },
        computed: {
            loading() {
                return this.parentComponent.loading;
            },
        },
    };
</script>
